import { computed, inject, Injectable } from '@angular/core';
import { FeatureFlagNames, FeatureFlagStore } from '../stores/feature-flags.store';
import { ThemeService } from './theme';

// ?v3=true forces V3 on; ?v3=false forces it off — takes precedence over the feature flag
function readUrlOverride(param: string): boolean | null {
  const value = new URLSearchParams(window.location.search).get(param);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly featureFlags = inject(FeatureFlagStore);
  private readonly themeService = inject(ThemeService);

  private readonly v3UrlOverride = readUrlOverride('v3');

  // ?token= is a QA/E2E path where auth comes from the URL instead of the parent
  // bridge. In this path authBridge.username() stays null so isLoggedIn$ never emits
  // true, updateContext is never called, and contextSynced$ never fires. Skip the
  // Unleash gate so the loader doesn't block indefinitely.
  private readonly hasUrlToken = new URLSearchParams(window.location.search).has('token');

  // One V3 flag per parent family, picked by theme. Mirrored in src/index.html's inline
  // script — keep both in sync.
  private readonly v3Flag = computed(() =>
    this.themeService.isFinnet() ? 'isV3FinnetEnabled' : 'isV3ZeroEnabled',
  );

  public readonly v3FlagName = computed(() => FeatureFlagNames[this.v3Flag()]);

  public isV3 = computed(() => this.v3UrlOverride ?? this.featureFlags[this.v3Flag()]());

  // True once we're confident the V3 flag won't flip again: either a URL override
  // is in effect (Unleash not involved), auth came from the URL token param (no bridge
  // to trigger updateContext), or Unleash has re-evaluated with the real userId after
  // auth resolved. Stays false until that second evaluation completes, preventing
  // a flash between the default experience and V3 caused by stale cached flag values.
  public isV3Ready = computed(
    () =>
      this.v3UrlOverride !== null ||
      this.hasUrlToken ||
      this.featureFlags.featureFlagsContextReady(),
  );
}
